import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader } from 'lucide-react';
import config from '../../config';
import CadreEtape from './CadreEtape';
import { boutonPrincipal } from './ParcoursInitial';
import { identifiantDeRattachement } from '../../utils/rattachementCompte';
import { metadonneesDuCompte } from '../../utils/metadonneesCompte';
import { buildAccountName } from '../../utils/accountNaming';

const API_URL = config.API_URL;

/**
 * Dernière étape : choisir un compte, le rattacher au site, ouvrir le tableau de bord.
 *
 * ## Un seul compte, et pourquoi
 *
 * Le parcours promet un tableau de bord qui montre quelque chose ; un seul compte y suffit. Les
 * suivants s'ajoutent depuis l'écran Comptes, où l'on voit ce qu'on fait — plutôt que de faire
 * cocher une longue liste à quelqu'un qui découvre l'outil.
 *
 * ## Le rattachement n'est pas une formalité
 *
 * Ajouter le compte ne suffit pas : tant qu'aucun site ne le porte, le client reste **invisible
 * dans le sélecteur** (la route qui les liste écarte les clients dont aucun site ne porte de
 * compte) et le dashboard consolidé n'a rien à croiser. C'est exactement l'écran creux que ce
 * parcours existe pour éviter. D'où l'ordre : ajouter, rattacher, puis seulement clore.
 *
 * ⚠️ L'identifiant écrit dépend du fournisseur — voir `utils/rattachementCompte.js`. Un numéro
 * nu pour Matomo ou Piano disparaîtrait à la lecture suivante, sans erreur.
 *
 * ## La clôture vient après le rattachement, jamais avant
 *
 * Le verrou du parcours ne se rouvre pas. Clore trop tôt, sur un échec de rattachement,
 * priverait la personne du seul écran capable de réparer. Le serveur refuse d'ailleurs de clore
 * tant que le compte n'est pas rattaché : ce contrôle est là pour que ni un rejeu ni une erreur
 * d'enchaînement ne puisse laisser un compte à moitié installé.
 */
function EtapeCompte({ token, etat, onAvance, onTermine }) {
  const [connecteurs, setConnecteurs] = useState([]);
  const [connecteurId, setConnecteurId] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [choisi, setChoisi] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const entete = { headers: { Authorization: `Bearer ${token}` } };

  const chargerConnecteurs = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/connectors`, entete);
      const liste = data?.connectors || [];
      setConnecteurs(liste);
      // Le plus récent : c'est celui que la personne vient de brancher.
      setConnecteurId((actuel) => actuel || liste[liste.length - 1]?.id || null);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de lire vos connecteurs');
    } finally {
      setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { chargerConnecteurs(); }, [chargerConnecteurs]);

  useEffect(() => {
    if (!connecteurId) return;
    let annule = false;
    (async () => {
      setChargement(true);
      setErreur(null);
      setComptes([]);
      setChoisi(null);
      try {
        const { data } = await axios.get(`${API_URL}/api/connectors/${connecteurId}/accounts`, entete);
        const liste = data?.accounts || [];
        if (annule) return;
        // Les comptes administrateurs (MCC) ne portent pas de campagnes : les proposer mènerait
        // à un tableau de bord vide, ce qui est précisément ce qu'on veut éviter ici.
        setComptes(liste.filter((c) => !c.isManager));
      } catch (e) {
        if (!annule) setErreur(e.response?.data?.error || 'Impossible de lire les comptes de ce connecteur');
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connecteurId]);

  const connecteur = connecteurs.find((c) => c.id === connecteurId);

  const valider = async () => {
    if (!choisi || !etat?.site?.id) return;
    setEnvoi(true);
    setErreur(null);

    const sourceId = String(choisi.id ?? choisi.sourceId ?? choisi.customerId ?? '');
    // Le nom porte l'identifiant pour les régies : c'est la seule information qui distingue
    // deux comptes homonymes dans les écrans qui les listent.
    const nom = buildAccountName(connecteur?.provider, choisi.name || choisi.sourceName || sourceId, sourceId);

    try {
      // 1. Le compte entre en bibliothèque. Un 409 signifie qu'il y était déjà — c'est le cas
      //    des connecteurs qui créent leurs comptes à la connexion : ce n'est pas un échec.
      try {
        await axios.post(`${API_URL}/api/library/add`, {
          type: connecteur?.provider,
          name: nom,
          sourceId,
          sourceName: nom,
          // Sans ces métadonnées, le compte s'enregistre sans erreur et ne rend aucune donnée :
          // chaque dashboard relit l'identifiant sous le nom que sa famille lui donne.
          metadata: metadonneesDuCompte(connecteur?.provider, sourceId, choisi),
          connectionId: connecteurId
        }, entete);
      } catch (e) {
        if (e.response?.status !== 409) throw e;
      }

      // 2. Le rattacher au site : sans cela, le client reste invisible et le consolidé vide.
      const identifiant = identifiantDeRattachement({
        type: connecteur?.provider,
        sourceId,
        connectionId: connecteurId
      });
      await axios.put(`${API_URL}/api/sites/${etat.site.id}`, {
        accountIds: [identifiant]
      }, entete);

      // 3. Clore, maintenant seulement.
      await axios.post(`${API_URL}/api/onboarding/terminer`, {}, entete);

      onTermine?.({ type: connecteur?.provider, sourceId });
    } catch (e) {
      const donnees = e.response?.data;
      setErreur(donnees?.errorCode === 'ACCOUNT_LIMIT_REACHED'
        ? `Vous avez atteint la limite de ${donnees.max} compte(s) de votre offre.`
        : donnees?.error || "Impossible d'ajouter ce compte");
      setEnvoi(false);
      await onAvance();
    }
  };

  const corps = () => {
    if (chargement) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#6b7280', padding: '20px' }}>
          <Loader className="spin" size={24} style={{ marginBottom: '10px' }} />
          <span style={{ fontSize: '14px' }}>Lecture de vos comptes…</span>
        </div>
      );
    }

    if (comptes.length === 0) {
      return (
        <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
          Ce connecteur n’ouvre aucun compte exploitable.
          {connecteurs.length > 1 && ' Choisissez-en un autre ci-dessus.'}
        </p>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
        {comptes.map((c) => {
          const id = String(c.id ?? c.sourceId ?? c.customerId ?? '');
          const actif = choisi && String(choisi.id ?? choisi.sourceId ?? choisi.customerId ?? '') === id;
          return (
            <button
              key={id}
              onClick={() => setChoisi(c)}
              style={{
                textAlign: 'left', padding: '12px 14px', borderRadius: '8px',
                border: `1px solid ${actif ? '#1a1a1a' : '#e5e7eb'}`,
                background: actif ? '#1a1a1a' : 'white',
                color: actif ? 'white' : '#1f2937',
                cursor: 'pointer'
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 500 }}>{c.name || c.sourceName || id}</div>
              <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
                {id}{c.currency ? ` · ${c.currency}` : ''}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <CadreEtape
      titre="Choisissez un compte"
      explication={`Il sera rattaché à « ${etat?.site?.nom || 'votre site'} ». Vous en ajouterez d’autres depuis l’écran Comptes.`}
      erreur={erreur}
      largeur="520px"
    >
      {connecteurs.length > 1 && (
        <select
          value={connecteurId || ''}
          onChange={(e) => setConnecteurId(e.target.value)}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginBottom: '14px',
            border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white'
          }}
        >
          {connecteurs.map((c) => (
            <option key={c.id} value={c.id}>{c.connectionName || c.provider}</option>
          ))}
        </select>
      )}

      {corps()}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '22px' }}>
        <button
          onClick={valider}
          disabled={!choisi || envoi}
          style={{
            ...boutonPrincipal,
            cursor: !choisi || envoi ? 'not-allowed' : 'pointer',
            opacity: !choisi || envoi ? 0.5 : 1
          }}
        >
          {envoi ? 'Installation…' : 'Ouvrir mon tableau de bord'}
        </button>
      </div>
    </CadreEtape>
  );
}

export default EtapeCompte;
